import React, { useState } from 'react';
import type { Vocabulary } from '../../types/vocabulary.types';
import { Card } from '../common/Card';
import { playWordPronunciation } from '../../utils/audio';

interface VocabularyCardProps {
  vocabulary: Vocabulary;
  onEdit: (vocab: Vocabulary) => void;
  onDelete: (vocab: Vocabulary) => void;
}

export function VocabularyCard({
  vocabulary,
  onEdit,
  onDelete,
}: VocabularyCardProps): React.ReactElement {
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  const formattedDate = new Date(vocabulary.createdAt).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Card padding="md" hover className="flex flex-col justify-between h-full group">
      <div>
        {/* Top Header: Word + IPA & Action buttons */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-white tracking-wide truncate">
                {vocabulary.word}
              </h3>
              <button
                id={`play-word-btn-${vocabulary.id}`}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  playWordPronunciation(
                    vocabulary.word,
                    vocabulary.audioUrl,
                    () => setIsPlayingAudio(true),
                    () => setIsPlayingAudio(false),
                  );
                }}
                title={`Listen to pronunciation of "${vocabulary.word}"`}
                aria-label={`Listen to ${vocabulary.word}`}
                className={`p-1 rounded-md transition-all ${
                  isPlayingAudio
                    ? 'text-primary-300 bg-primary-500/20 ring-1 ring-primary-400/40'
                    : 'text-slate-400 hover:text-primary-300 hover:bg-white/5'
                }`}
              >
                <svg
                  className={`w-4 h-4 ${isPlayingAudio ? 'animate-pulse text-primary-400' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                </svg>
              </button>
              {vocabulary.ipa && (
                <span className="inline-block font-mono text-xs text-primary-300 bg-primary-500/10 border border-primary-500/20 px-2 py-0.5 rounded-md">
                  {vocabulary.ipa}
                </span>
              )}
            </div>
            <p className="text-sm font-medium text-accent-300 mt-1">
              {vocabulary.meaningVi}
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1 opacity-90 group-hover:opacity-100 transition-opacity shrink-0">
            <button
              id={`edit-btn-${vocabulary.id}`}
              onClick={() => onEdit(vocabulary)}
              title="Edit vocabulary"
              className="p-1.5 rounded-lg text-slate-400 hover:text-primary-300 hover:bg-primary-500/10 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
              </svg>
            </button>
            <button
              id={`delete-btn-${vocabulary.id}`}
              onClick={() => onDelete(vocabulary)}
              title="Delete vocabulary"
              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
              </svg>
            </button>
          </div>
        </div>

        {/* Groups / Collections Badges */}
        {vocabulary.groups && vocabulary.groups.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {vocabulary.groups.map((group) => (
              <span
                key={group.id}
                id={`group-badge-${group.id}`}
                className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-indigo-500/15 text-indigo-300 border border-indigo-500/30"
              >
                <svg className="w-2.5 h-2.5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span>{group.name}</span>
              </span>
            ))}
          </div>
        )}

        {/* Synonyms */}
        {vocabulary.synonyms.length > 0 && (
          <div className="mt-3">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400 block mb-1">
              Synonyms
            </span>
            <div className="flex flex-wrap gap-1.5">
              {vocabulary.synonyms.map((syn, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-300 border border-primary-500/20"
                >
                  {syn}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Collocations */}
        {vocabulary.collocations.length > 0 && (
          <div className="mt-2.5">
            <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400 block mb-1">
              Collocations
            </span>
            <div className="flex flex-wrap gap-1.5">
              {vocabulary.collocations.map((col, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                >
                  {col}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Example sentence */}
        {vocabulary.exampleSentence && (
          <div className="mt-3.5 p-2.5 rounded-xl bg-surface-900/60 border-l-2 border-primary-500 text-xs text-slate-300 italic">
            &ldquo;{vocabulary.exampleSentence}&rdquo;
          </div>
        )}
      </div>

      {/* Footer Timestamp */}
      <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-slate-400">
        <span>Added {formattedDate}</span>
      </div>
    </Card>
  );
}
